{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "$id": "ctas",
  "x-cli-editable": true,
  "x-feature": false,
  "title": "CTAs",
  "description": "Configure widget CTA buttons, additional CTA URLs, and tracking behavior.",
  "type": "object",
  "properties": {
    "ctas": {
      "type": "array",
      "ui:separatorBefore": true,
      "ui:sectionTitle": "Widget CTAs",
      "ui:sectionIcon": "MousePointerClick",
      "ui:sectionDescription": "The call-to-action buttons Rose shows inside the chat and search bar (for example 'Book a demo' or 'Try for free'). Each button has a destination URL and label per language. Clicks on these are tracked automatically.",
      "ui:title": "Buttons",
      "ui:description": "Add one entry per CTA: a unique id, the destination URL, and the button label for each language.",
      "maxItems": 20,
      "items": {
        "type": "object",
        "properties": {
          "cta_id": {
            "type": "string",
            "ui:description": "Unique CTA identifier (lowercase, numbers, and hyphens only)",
            "maxLength": 50,
            "pattern": "^[a-z0-9][a-z0-9-]*$"
          },
          "url": {
            "type": "object",
            "ui:description": "CTA URL by language code",
            "additionalProperties": {
              "type": "string",
              "format": "uri",
              "pattern": "\\.[a-zA-Z]{2,}"
            }
          },
          "text": {
            "type": "object",
            "ui:description": "CTA button text by language code",
            "additionalProperties": {
              "type": "string",
              "maxLength": 200,
              "minLength": 1
            }
          },
          "post_booking_redirect_url": {
            "type": "string",
            "x-tier": "staff",
            "format": "uri",
            "pattern": "\\.[a-zA-Z]{2,}",
            "ui:description": "Only used when this CTA opens an in-chat booking calendar. After the visitor completes a booking inside the embedded calendar, the widget redirects them to this URL so the page's existing thank_you_pages conversion tracking and post-conversion qualification flow kick in. Leave blank to keep the visitor in chat after booking (calendar closes, no redirect). Example: https://example.com/thank-you."
          },
          "show_on_intents": {
            "type": "array",
            "ui:description": "Show this CTA only after an answer to a visitor message with one of these intents, in addition to the regular CTAs. Leave empty to always show it. Example: support, for a help-center CTA.",
            "uniqueItems": true,
            "items": {
              "type": "string",
              "enum": [
                "support"
              ]
            }
          },
          "show_on_pages": {
            "type": "array",
            "x-tier": "advanced",
            "ui:description": "Show this CTA only on pages matching one of these URL patterns. Use * as a wildcard, e.g. 'https://www.example.com/fr/shop*'. On a matching page it replaces the CTAs with no patterns, which stay the fallback everywhere else.",
            "uniqueItems": true,
            "maxItems": 50,
            "items": {
              "type": "string",
              "minLength": 1,
              "maxLength": 500,
              "pattern": "^(\\*|https?://)"
            }
          }
        },
        "required": [
          "cta_id",
          "url"
        ]
      }
    },
    "alias_overrides": {
      "type": "object",
      "ui:hidden": true,
      "ui:description": "Sparse CTA URL and label overrides for configured alias domains. Managed through the curated CTA editor.",
      "default": {},
      "additionalProperties": {
        "type": "object",
        "additionalProperties": {
          "type": "object",
          "properties": {
            "url": {
              "type": "object",
              "additionalProperties": {
                "type": "string",
                "format": "uri",
                "pattern": "\\.[a-zA-Z]{2,}"
              }
            },
            "text": {
              "type": "object",
              "additionalProperties": {
                "type": "string",
                "maxLength": 200,
                "minLength": 1
              }
            }
          }
        }
      }
    },
    "inline_cta_on_demo_mode": {
      "type": "string",
      "x-access": "staff",
      "enum": [
        "auto",
        "always",
        "never"
      ],
      "ui:title": "Inline demo CTA",
      "ui:widget": "radio-group",
      "ui:enumLabels": {
        "auto": "Auto",
        "always": "Always inline",
        "never": "Never inline"
      },
      "ui:description": "Where the demo CTA shows after Rose offers a demo: inside the chat (inline) or in the search-bar strip. Auto uses inline only when a single widget CTA is configured.",
      "default": "auto"
    },
    "inline_cta_on_demo": {
      "type": "boolean",
      "ui:hidden": true,
      "ui:description": "Legacy boolean retained for old client overrides. Use Inline demo CTA mode instead."
    },
    "additional_cta_urls": {
      "type": "array",
      "x-tier": "advanced",
      "ui:separatorBefore": true,
      "ui:sectionTitle": "Website CTA",
      "ui:sectionIcon": "Link2",
      "ui:sectionDescription": "Track clicks on CTA buttons already on your website. Rose matches them by where they link to, not the page they sit on. The Widget CTAs above are detected automatically.",
      "ui:title": "Additional CTA destinations",
      "ui:tooltip": "Extra web addresses your CTA buttons link TO — not the pages they appear on. Only real links (<a href>) match, not JavaScript-only buttons.",
      "ui:description": "Add CTA destinations not covered by the buttons above — the address each button opens. One entry covers that button everywhere on your site. Use * as a wildcard, e.g. 'https://app.example.com/signup*'.",
      "maxItems": 50,
      "items": {
        "type": "object",
        "properties": {
          "cta_id": {
            "type": "string",
            "ui:description": "Unique identifier for this additional CTA URL (lowercase, numbers, and hyphens only)",
            "maxLength": 50,
            "pattern": "^[a-z0-9][a-z0-9-]*$"
          },
          "url": {
            "type": "object",
            "ui:description": "Additional CTA URL by language code",
            "additionalProperties": {
              "type": "string",
              "format": "uri",
              "pattern": "\\.[a-zA-Z]{2,}"
            }
          }
        },
        "required": [
          "cta_id",
          "url"
        ]
      }
    },
    "cta_click_selectors": {
      "type": "array",
      "x-tier": "advanced",
      "ui:title": "Button CTAs (by selector)",
      "ui:tooltip": "For CTA buttons that are NOT links (e.g. Webflow / JavaScript buttons with no destination URL).",
      "ui:description": "Only for CTA buttons that navigate with JavaScript and have no real link to match above. Give a CSS selector for the button; a click on it counts as a CTA click. Selectors can break on redesigns, so prefer the destinations above for real links. Example: selector '.signup-button', name 'Free trial'.",
      "maxItems": 50,
      "items": {
        "type": "object",
        "properties": {
          "selector": {
            "type": "string",
            "minLength": 1,
            "ui:description": "CSS selector that matches the button, e.g. '.signup-button' or '[data-cta=free-trial]'. Find it by right-clicking the button and choosing Inspect."
          },
          "text": {
            "type": "array",
            "maxItems": 20,
            "items": {
              "type": "string",
              "maxLength": 200
            },
            "ui:description": "Optional. When several buttons share the same selector, list the button's visible text so ONLY that button is tracked. On multilingual sites, add each language's wording, e.g. 'Essai gratuit' and 'Free trial' — a click matches if the button's text contains ANY entry. Case-insensitive substring match."
          },
          "label": {
            "type": "string",
            "maxLength": 200,
            "ui:description": "A name for this CTA, shown in your analytics (e.g. 'Free trial'). Optional — falls back to the button's text."
          }
        },
        "required": [
          "selector"
        ]
      }
    },
    "cta_click_tracking_enabled": {
      "type": "boolean",
      "x-tier": "advanced",
      "ui:title": "Count CTA link clicks",
      "ui:tooltip": "Count clicks on your CTA links without changing the links.",
      "ui:description": "Counts every click on your configured CTA links (Rose CTA destinations and the Website CTA URLs above) in your analytics. Your links are left unchanged. Only real links (<a href>) are counted.",
      "default": true
    },
    "in_place_url_attribution_enabled": {
      "type": "boolean",
      "x-tier": "advanced",
      "ui:separatorBefore": true,
      "ui:sectionTitle": "URL attribution",
      "ui:sectionIcon": "Globe",
      "ui:sectionDescription": "Controls whether Rose forwards URL parameters and adds visitor/session identifiers for downstream attribution.",
      "ui:title": "In-place Rose URL parameters",
      "ui:tooltip": "Add Rose visitor/session IDs to the address bar of the page the visitor is currently on.",
      "ui:description": "Adds Rose visitor/session IDs to the page the visitor is currently on — the address-bar URL is rewritten in place, no click needed. Use this so analytics or forms on that same page can read the IDs.",
      "default": false
    },
    "cta_link_attribution_enabled": {
      "type": "boolean",
      "x-tier": "advanced",
      "ui:title": "Decorate CTA links",
      "ui:tooltip": "Add Rose visitor/session IDs to the links that leave your site, so they travel to the destination on click.",
      "ui:description": "Adds the same Rose visitor/session IDs to the links that leave your site — your configured CTA destinations (Rose CTA destinations and the Website CTA URLs) — so the IDs travel to the destination page when a visitor clicks and a conversion there can be credited to Rose. Only real links (<a href>) are decorated.",
      "default": false
    },
    "parameters_forwarding": {
      "type": "string",
      "x-tier": "advanced",
      "enum": [
        "none",
        "allowlist",
        "all"
      ],
      "ui:title": "Parameters forwarding",
      "ui:widget": "radio-group",
      "ui:enumLabels": {
        "none": "None",
        "allowlist": "Only allowlist",
        "all": "All"
      },
      "ui:enumDescriptions": {
        "none": "Carry none of the visitor's URL parameters to the CTA destination.",
        "allowlist": "Carry only the parameters listed below (recommended — covers the common campaign and ad tags).",
        "all": "Carry every parameter from the current page, without overwriting ones already on the CTA link."
      },
      "ui:tooltip": "Carry the visitor's campaign / ad tracking tags (utm_source, gclid…) through to the CTA destination.",
      "ui:description": "When a visitor arrives on your page with campaign or ad tags in the URL (for example utm_source, gclid, fbclid) and then clicks a CTA, Rose copies those tags onto the destination link. That way the page they land on — and your analytics or CRM — still know which campaign or ad brought them in, instead of losing that at the click. Always applies to Rose's widget CTA buttons; it also applies to your own website CTA links when Decorate CTA links above is on.",
      "default": "allowlist"
    },
    "parameters_forwarding_allowlist": {
      "type": "array",
      "x-tier": "advanced",
      "ui:title": "Parameters forwarding allowlist",
      "ui:widget": "tags-input",
      "ui:placeholder": "Add parameter",
      "ui:description": "URL parameter names to forward when Parameters forwarding is set to Only allowlist. Defaults to common attribution parameters.",
      "ui:show-when": {
        "parameters_forwarding": "allowlist"
      },
      "uniqueItems": true,
      "maxItems": 50,
      "items": {
        "type": "string",
        "minLength": 1,
        "maxLength": 100,
        "pattern": "^[^\\s=&?#]+$"
      },
      "default": [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "utm_id",
        "gclid",
        "gclsrc",
        "gbraid",
        "wbraid",
        "gad_source",
        "gad_campaignid",
        "dclid",
        "fbclid",
        "msclkid",
        "li_fat_id",
        "ttclid",
        "twclid",
        "ScCid",
        "epik"
      ]
    }
  },
  "default": {
    "inline_cta_on_demo_mode": "auto",
    "ctas": [],
    "alias_overrides": {},
    "additional_cta_urls": [],
    "cta_click_tracking_enabled": true,
    "cta_link_attribution_enabled": false,
    "in_place_url_attribution_enabled": false,
    "parameters_forwarding": "allowlist",
    "parameters_forwarding_allowlist": [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      "utm_id",
      "gclid",
      "gclsrc",
      "gbraid",
      "wbraid",
      "gad_source",
      "gad_campaignid",
      "dclid",
      "fbclid",
      "msclkid",
      "li_fat_id",
      "ttclid",
      "twclid",
      "ScCid",
      "epik"
    ]
  }
}
